import { useState, useEffect } from 'react';
import axios from 'axios';

interface CustomOfferFormData {
	name: string;
	quantity: number;
	priceQuote: number;
	interval: 'month' | 'year';
	intervalCount: number;
	offerSubType: 'inbound' | 'outbound';
	productName: string;
	trialPeriodDays?: number;
	saveDefault: boolean;
	type: 'perSeat' | 'unlimited';
	features: {
		voiceCredits: number;
		smsCredits: number;
	};
}

interface CustomOffer {
	id: string;
	quantity: number;
	trialPeriodDays?: number;
	intervalCount: number;
	interval: string;
	priceQuote: number;
	URL: string;
}

const baseUrl = import.meta.env.VITE_APP_API_BASE_URL;

const CustomOffers = () => {
	const [isLoading, setIsLoading] = useState(false);
	const [offers, setOffers] = useState<CustomOffer[]>([]);
	const [generatedLink, setGeneratedLink] = useState<string>('');
	const [showTrialPeriod, setShowTrialPeriod] = useState(false);
	const [showFeatures, setShowFeatures] = useState(false);
	const [showModal, setShowModal] = useState(false);
	const [formData, setFormData] = useState<CustomOfferFormData>({
		name: '',
		quantity: 1,
		priceQuote: 500,
		interval: 'month',
		intervalCount: 1,
		offerSubType: 'inbound',
		productName: '',
		trialPeriodDays: 7,
		saveDefault: false,
		type: 'unlimited',
		features: {
			voiceCredits: 3000,
			smsCredits: 500,
		},
	});

	useEffect(() => {
		fetchOffers();
	}, []);

	// Update quantity when type changes
	useEffect(() => {
		if (formData.type === 'unlimited') {
			setFormData((prev) => ({ ...prev, quantity: 1 }));
		}
	}, [formData.type]);

	const fetchOffers = async () => {
		try {
			setIsLoading(true);
			const token = localStorage.getItem('ADMIN_AUTH_TOKEN');
			const response = await axios.post(
				`${baseUrl}/api/custom-offers`,
				{},
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				},
			);

			if (response.data.success) {
				setOffers(response.data.data);
			}
		} catch (error) {
			console.error('Failed to fetch offers:', error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		try {
			setIsLoading(true);
			const token = localStorage.getItem('ADMIN_AUTH_TOKEN');

			// Transform form data to match API structure
			const apiData: any = {
				name: formData.name,
				quantity: formData.quantity,
				priceQuote: formData.priceQuote,
				interval: formData.interval,
				intervalCount: formData.intervalCount,
				offerSubType: 'inbound',
				productName: formData.productName,
				saveDefault: true,
				type: formData.type,
			};

			// Add trial period if enabled
			if (showTrialPeriod) {
				apiData.trialPeriodDays = formData.trialPeriodDays;
			}

			// Add features if enabled
			if (showFeatures) {
				apiData.features = {
					freeCreditsAllocated: {
						voiceCredits: formData.features.voiceCredits,
						smsCredits: formData.features.smsCredits,
					},
				};
			}

			const response = await axios.post(
				`${baseUrl}/api/custom-offers/generate-custom-offer-url`,
				apiData,
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				},
			);

			if (response.data.success) {
				setGeneratedLink(response.data.link);
				setShowModal(true);
				// Refresh the offers list
				await fetchOffers();
			}
		} catch (error) {
			console.error('Failed to generate offer:', error);
		} finally {
			setIsLoading(false);
		}
	};

	const copyToClipboard = (text: string) => {
		navigator.clipboard.writeText(text);
		// You could add a toast notification here
	};

	const formatPrice = (price: number) => {
		return new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'USD',
		}).format(price);
	};

	return (
		<div className="max-w-6xl mx-auto p-6">
			<h1 className="text-3xl font-bold mb-8">Custom Offer Management</h1>

			{/* Generate New Offer Form */}
			<div className="bg-white rounded-lg shadow-md p-6 mb-8">
				<h2 className="text-2xl font-semibold mb-6">Generate New Custom Offer</h2>

				<form onSubmit={handleSubmit} className="space-y-6">
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
						{/* Basic Information */}
						<div className="space-y-4">
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-2">
									Client Name (Optional)
								</label>
								<input
									type="text"
									value={formData.name}
									onChange={(e) => setFormData({ ...formData, name: e.target.value })}
									className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
									placeholder="Enter client name"
								/>
							</div>

							<div>
								<label className="block text-sm font-medium text-gray-700 mb-2">Product Name</label>
								<input
									type="text"
									value={formData.productName}
									onChange={(e) => setFormData({ ...formData, productName: e.target.value })}
									className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
									placeholder="Custom offer name"
									required
								/>
							</div>

							{/* Type Selection */}
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
								<div className="space-y-2">
									<label className="flex items-center">
										<input
											type="radio"
											name="type"
											value="unlimited"
											checked={formData.type === 'unlimited'}
											onChange={(e) =>
												setFormData({
													...formData,
													type: e.target.value as 'perSeat' | 'unlimited',
												})
											}
											className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
										/>
										<span className="ml-2 text-sm text-gray-900">Unlimited</span>
									</label>
									<label className="flex items-center">
										<input
											type="radio"
											name="type"
											value="perSeat"
											checked={formData.type === 'perSeat'}
											onChange={(e) =>
												setFormData({
													...formData,
													type: e.target.value as 'perSeat' | 'unlimited',
												})
											}
											className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
										/>
										<span className="ml-2 text-sm text-gray-900">Per Seat</span>
									</label>
								</div>
							</div>

							{/* Quantity - only show for perSeat type */}
							{formData.type === 'perSeat' && (
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-2">Quantity</label>
									<input
										type="number"
										min="1"
										value={formData.quantity}
										onChange={(e) =>
											setFormData({ ...formData, quantity: parseInt(e.target.value) })
										}
										className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
										required
									/>
								</div>
							)}

							<div>
								<label className="block text-sm font-medium text-gray-700 mb-2">
									Price Quote (USD)
								</label>
								<input
									type="number"
									min="0"
									step="0.01"
									value={formData.priceQuote}
									onChange={(e) =>
										setFormData({ ...formData, priceQuote: parseFloat(e.target.value) })
									}
									className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
									required
								/>
							</div>
						</div>

						{/* Billing and Type */}
						<div className="space-y-4">
							<div className="grid grid-cols-2 gap-4">
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-2">
										Billing Interval
									</label>
									<select
										value={formData.interval}
										onChange={(e) =>
											setFormData({ ...formData, interval: e.target.value as 'month' | 'year' })
										}
										className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
									>
										<option value="month">Month</option>
										<option value="year">Year</option>
									</select>
								</div>

								<div>
									<label className="block text-sm font-medium text-gray-700 mb-2">
										Interval Count
									</label>
									<input
										type="number"
										min="1"
										value={formData.intervalCount}
										onChange={(e) =>
											setFormData({ ...formData, intervalCount: parseInt(e.target.value) })
										}
										className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
										required
									/>
								</div>
							</div>

							<div className="flex items-center">
								<input
									type="checkbox"
									id="trialPeriod"
									checked={showTrialPeriod}
									onChange={(e) => setShowTrialPeriod(e.target.checked)}
									className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
								/>
								<label htmlFor="trialPeriod" className="ml-2 block text-sm text-gray-900">
									Include Trial Period
								</label>
							</div>

							{showTrialPeriod && (
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-2">
										Trial Period (Days)
									</label>
									<input
										type="number"
										min="1"
										value={formData.trialPeriodDays}
										onChange={(e) =>
											setFormData({ ...formData, trialPeriodDays: parseInt(e.target.value) })
										}
										className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
									/>
								</div>
							)}
						</div>
					</div>

					{/* Features Section */}
					<div className="border-t pt-6">
						<div className="flex items-center mb-4">
							<input
								type="checkbox"
								id="showFeatures"
								checked={showFeatures}
								onChange={(e) => setShowFeatures(e.target.checked)}
								className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
							/>
							<label
								htmlFor="showFeatures"
								className="ml-2 block text-lg font-medium text-gray-900"
							>
								Include Features
							</label>
						</div>

						{showFeatures && (
							<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-2">
										Voice Credits
									</label>
									<input
										type="number"
										min="0"
										value={formData.features.voiceCredits}
										onChange={(e) =>
											setFormData({
												...formData,
												features: { ...formData.features, voiceCredits: parseInt(e.target.value) },
											})
										}
										className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
									/>
								</div>

								<div>
									<label className="block text-sm font-medium text-gray-700 mb-2">
										SMS Credits
									</label>
									<input
										type="number"
										min="0"
										value={formData.features.smsCredits}
										onChange={(e) =>
											setFormData({
												...formData,
												features: { ...formData.features, smsCredits: parseInt(e.target.value) },
											})
										}
										className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
									/>
								</div>
							</div>
						)}
					</div>

					<div className="flex justify-end">
						<button
							type="submit"
							disabled={isLoading}
							className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
						>
							{isLoading ? 'Generating...' : 'Generate Offer URL'}
						</button>
					</div>
				</form>
			</div>

			{/* Existing Offers Table */}
			<div className="bg-white rounded-lg shadow-md p-6">
				<h2 className="text-2xl font-semibold mb-6">Existing Custom Offers</h2>

				{isLoading ? (
					<div className="text-center py-8">Loading offers...</div>
				) : offers.length === 0 ? (
					<div className="text-center py-8 text-gray-500">No custom offers found.</div>
				) : (
					<div className="overflow-x-auto">
						<table className="min-w-full divide-y divide-gray-200">
							<thead className="bg-gray-50">
								<tr>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										ID
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Type
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Quantity
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Price
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Billing
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Trial
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										URL
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Actions
									</th>
								</tr>
							</thead>
							<tbody className="bg-white divide-y divide-gray-200">
								{offers.map((offer) => (
									<tr key={offer.id} className="hover:bg-gray-50">
										<td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
											{offer.id.slice(0, 8)}...
										</td>
										<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
											{offer.quantity === 1 ? 'Unlimited' : 'Per Seat'}
										</td>
										<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
											{offer.quantity === 1 ? 'Unlimited' : offer.quantity}
										</td>
										<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
											{formatPrice(offer.priceQuote)}
										</td>
										<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
											Every {offer.intervalCount} {offer.interval}
											{offer.intervalCount > 1 ? 's' : ''}
										</td>
										<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
											{offer.trialPeriodDays ? `${offer.trialPeriodDays} days` : 'No trial'}
										</td>
										<td className="px-6 py-4 text-sm text-gray-900">
											<div className="max-w-xs truncate">{offer.URL}</div>
										</td>
										<td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
											<button
												onClick={() => copyToClipboard(offer.URL)}
												className="text-indigo-600 hover:text-indigo-900"
											>
												Copy URL
											</button>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>

			{/* Success Modal */}
			{showModal && (
				<div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
					<div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
						<div className="mt-3 text-center">
							<div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
								<svg
									className="h-6 w-6 text-green-600"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth="2"
										d="M5 13l4 4L19 7"
									/>
								</svg>
							</div>
							<h3 className="text-lg leading-6 font-medium text-gray-900 mt-4">
								Offer Generated Successfully!
							</h3>
							<div className="mt-4">
								<p className="text-sm text-gray-500 mb-4">
									Your custom offer URL has been created:
								</p>
								<div className="bg-gray-50 p-3 rounded-md">
									<input
										type="text"
										value={generatedLink}
										readOnly
										className="w-full text-sm bg-transparent border-none outline-none"
									/>
								</div>
							</div>
							<div className="flex justify-center space-x-3 mt-6">
								<button
									onClick={() => copyToClipboard(generatedLink)}
									className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
								>
									Copy URL
								</button>
								<button
									onClick={() => setShowModal(false)}
									className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
								>
									Close
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default CustomOffers;
